import React, { useCallback } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { ENERGY_MASTERS_TENANT_ID } from 'clipsal-cortex-utils/src/constants/common-constants';

import { useViewportType } from '../../common/hooks/use-viewport-type';
import { selectSite } from '../site/siteSlice';
import { openCortexSupportPage } from './open-support-page';
import { SupportModal } from './SupportModal';

/**
 * Shared handler for every in-app "Contact Support" entry point.
 *
 * - Energy Masters sites open the {@link SupportModal} (phone/email/FAQ contact details).
 * - All other sites are sent to the external Cortex support page via {@link openCortexSupportPage}.
 *
 * Usage:
 * ```tsx
 * const { onContactSupport, supportModal } = useContactSupport();
 * <Button onClick={onContactSupport}>Contact Support</Button>
 * {supportModal}
 * ```
 */
export function useContactSupport() {
  const site = useSelector(selectSite);
  const isEnergyMastersSite = site.tenant_id === ENERGY_MASTERS_TENANT_ID;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isDesktopViewport } = useViewportType();

  const onContactSupport = useCallback(() => {
    if (isEnergyMastersSite) {
      onOpen();
    } else {
      openCortexSupportPage(isDesktopViewport);
    }
  }, [isEnergyMastersSite, onOpen, isDesktopViewport]);

  // Only Energy Masters sites ever open the modal; other sites navigate away to the support page.
  const supportModal = isEnergyMastersSite ? <SupportModal isOpen={isOpen} onClose={onClose} /> : null;

  return { onContactSupport, supportModal };
}
